import React, { createContext, useContext } from "react";
import type { JSONSchema7 } from "json-schema";
import type { FieldMapping } from "@/features/reports/config/fieldMapping";
import type { VideoData } from "../../shared/interfaces/VideoData";
import type { OfficerProfile } from "../../shared/interfaces/OfficerProfile";
import type { EforceIncidentModel } from "../../shared/interfaces/EforceModels";

/**
 * The edit page's dependency surface, replacing the 21-prop
 * ExtractedInformationProps drilling. The provider is a dumb memoized value
 * carrier: it owns NO fetching, NO WebSocket, and NO effects —
 * EditSectionPage keeps composing useReportData/useReportWebSocket/
 * useReportSaving and feeds the value in. That is what structurally
 * guarantees the read-only preview twin (ExtractedInfo/ExtractedInformation,
 * which has its own local props) can never acquire an edit socket through
 * this context.
 *
 * Deliberately NOT in the context: formData/currentSection (zustand
 * selectors via useFormDataStore), toast (useToast), per-section locals
 * (itemRefs, sectionTitle).
 */
export interface EditSectionContextValue {
  /** Report identity + review state. */
  report: {
    reportId: string;
    reportType: string;
    rms: string;
    status: string;
    allowEditing: boolean;
    isReportOwner: boolean;
    showRMSCommit: boolean;
    /**
     * Server-driven narrative-only mode: the full schema-bound report has not
     * been generated yet. When true the page renders NarrativeOnlyEditor (no
     * schema) instead of the multi-section form.
     */
    narrativeOnly: boolean;
  };
  /**
   * Resolved schema + field mapping. Both are absent in narrative-only mode
   * (the schema is never fetched) — only the full schema form consumes them.
   */
  schema: {
    jsonSchema: JSONSchema7 | null;
    schemaMapping?: Record<string, FieldMapping>;
  };
  video: {
    data: VideoData | VideoData[] | null;
    loadStatus: "loading" | "loaded" | "error";
  };
  /** Save pipeline — pure pass-through of the WS save protocol. */
  save: {
    isSaving: boolean;
    /** was onUpdate: zustand write + optional WS save */
    updateFormData: (updated: EforceIncidentModel, save: boolean) => Promise<void>;
    /** was handleSaveChanges / handleSaveCurrentChanges */
    saveChanges: (
      payloadOverride?: Partial<EforceIncidentModel>,
    ) => Promise<void>;
    /** was onSubmit: save (awaiting the ack) + navigate to /final-edits */
    submit: () => Promise<void>;
    /**
     * Triggers full-report generation from a narrative-only report. Resolves
     * when the run has been kicked off; the editor flips to full mode once the
     * background run completes. Only meaningful in narrative-only mode.
     */
    generateFullReport: () => Promise<void>;
    /** True while the full-report run is in flight. */
    isGeneratingFull: boolean;
    /** Wall-clock start (ms) of the in-flight full-report run, else null. */
    generationStartedAt: number | null;
    /** Budget (ms) the full-report run is given — drives the progress timer. */
    generationTimeoutMs: number;
  };
  /** Live editing session (lock heartbeat + activity log). */
  session: {
    liveWebSocket: React.MutableRefObject<WebSocket | null>;
    logUserActivity: (action: string) => void;
  };
  /** Agent round-trip: snapshot-capturing refetch + the snapshot ref. */
  agent: {
    refreshReportData: () => void | Promise<void>;
    formDataSnapshotRef: React.MutableRefObject<EforceIncidentModel | null>;
  };
  /** Chat bridge between form widgets and the chat panel. */
  chat: {
    sendMessageRef: React.MutableRefObject<
      ((message: string) => Promise<void>) | null
    >;
    setShowChat: (show: boolean) => void;
    available: boolean;
  };
  userProfile: OfficerProfile | null;
}

const EditSectionContext = createContext<EditSectionContextValue | null>(null);

export const EditSectionProvider: React.FC<{
  value: EditSectionContextValue;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <EditSectionContext.Provider value={value}>
    {children}
  </EditSectionContext.Provider>
);

export function useEditSection(): EditSectionContextValue {
  const value = useContext(EditSectionContext);
  if (!value) {
    throw new Error(
      "useEditSection must be used inside EditSectionProvider (the edit page's loaded subtree).",
    );
  }
  return value;
}
